import React, { useState, useEffect } from 'react';
import { createPortal } from 'react-dom';
import { useNavigate } from 'react-router-dom';
import './HonorHall.css';
import { useTranslation } from 'react-i18next';
import { FriendsIcon } from './friends';
import { SettingsMenu } from './settings';
import { useMusic } from './hooks/useMusic';
import { playSoundEffect } from './hooks/useSoundEffects';
import { API_URL } from './config/apiConfig';
import { useContainerScale } from './hooks/useViewportScale';
import { TutorialMessageBox } from './tutorial/index';

function HonorHall({ user, onLogout }) {
  const navigate = useNavigate();
  const { t } = useTranslation();
  
  // Viewport scaling for responsive design
  const { 
    scale: viewportScale, 
    offsetX: viewportOffsetX, 
    offsetY: viewportOffsetY,
    baseWidth: containerBaseWidth,
    baseHeight: containerBaseHeight,
    isMobile: isContainerMobile
  } = useContainerScale();
  
  // Play honor hall music starting at 11 seconds
  useMusic('/music/honor_hall.mp3', 11);
  const [activeTab, setActiveTab] = useState('battle-legends');
  const [assetStrategiesData, setAssetStrategiesData] = useState([]);
  const [trainingStrategiesData, setTrainingStrategiesData] = useState([]);
  const [battleStrategiesData, setBattleStrategiesData] = useState([]);
  const [battleLegendsData, setBattleLegendsData] = useState([]);
  const [traineesOfMonthData, setTraineesOfMonthData] = useState([]);
  const [warHeroesData, setWarHeroesData] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [currentPage, setCurrentPage] = useState(1);
  const [userRank, setUserRank] = useState(null);
  const [showUserRank, setShowUserRank] = useState(false);
  const [showTutorialHonorHall, setShowTutorialHonorHall] = useState(false);
  const [seenTutorialMessages, setSeenTutorialMessages] = useState([]);
  
  const itemsPerPage = 100;

  // Determine if current tab shows players or strategies
  const isPlayerTab = activeTab === 'battle-legends' || activeTab === 'trainees-of-month' || activeTab === 'war-heroes';
  const isStrategyTab = !isPlayerTab;

  useEffect(() => {
    fetchData();
  }, []);

  useEffect(() => {
    const token = localStorage.getItem('token');
    if (!token) return;
    fetch(`${API_URL}/tutorial/status`, { headers: { Authorization: `Bearer ${token}` } })
      .then(res => res.ok ? res.json() : null)
      .then(data => {
        if (data) setSeenTutorialMessages(data.seenMessages || []);
      })
      .catch(() => {});
  }, [user]);

  useEffect(() => {
    if (user && !loading && !seenTutorialMessages.includes('honor_hall')) {
      const timer = setTimeout(() => setShowTutorialHonorHall(true), 600);
      return () => clearTimeout(timer);
    }
  }, [user, loading, seenTutorialMessages]);

  const fetchData = async () => {
    setLoading(true);
    try {
      const token = localStorage.getItem('token');
      const [assetStrategiesResponse, trainingStrategiesResponse, battleStrategiesResponse, battleLegendsResponse, traineesOfMonthResponse, warHeroesResponse] = await Promise.all([
        fetch(`${API_URL}/honor-hall/asset-strategies`, {
          headers: { Authorization: `Bearer ${token}` }
        }),
        fetch(`${API_URL}/honor-hall/training-strategies`, {
          headers: { Authorization: `Bearer ${token}` }
        }),
        fetch(`${API_URL}/honor-hall/battle-strategies`, {
          headers: { Authorization: `Bearer ${token}` }
        }),
        fetch(`${API_URL}/honor-hall/battle-legends`, {
          headers: { Authorization: `Bearer ${token}` }
        }),
        fetch(`${API_URL}/honor-hall/trainees-of-month`, {
          headers: { Authorization: `Bearer ${token}` }
        }),
        fetch(`${API_URL}/honor-hall/war-heroes`, {
          headers: { Authorization: `Bearer ${token}` }
        })
      ]);

      if (!assetStrategiesResponse.ok || !trainingStrategiesResponse.ok || !battleStrategiesResponse.ok || !battleLegendsResponse.ok || !traineesOfMonthResponse.ok || !warHeroesResponse.ok) {
        throw new Error('Failed to fetch honor hall data');
      }

      const assetStrategies = await assetStrategiesResponse.json();
      const trainingStrategies = await trainingStrategiesResponse.json();
      const battleStrategies = await battleStrategiesResponse.json();
      const battleLegends = await battleLegendsResponse.json();
      const traineesOfMonth = await traineesOfMonthResponse.json();
      const warHeroes = await warHeroesResponse.json();

      // Ensure we have arrays
      setAssetStrategiesData(Array.isArray(assetStrategies) ? assetStrategies : []);
      setTrainingStrategiesData(Array.isArray(trainingStrategies) ? trainingStrategies : []);
      setBattleStrategiesData(Array.isArray(battleStrategies) ? battleStrategies : []);
      setBattleLegendsData(Array.isArray(battleLegends) ? battleLegends : []);
      setTraineesOfMonthData(Array.isArray(traineesOfMonth) ? traineesOfMonth : []);
      setWarHeroesData(Array.isArray(warHeroes) ? warHeroes : []);

      // Find user's rank in player tabs
      if (Array.isArray(battleLegends)) {
        const userBattleLegendsRank = battleLegends.findIndex(player => player.username === user?.username);
        if (userBattleLegendsRank !== -1) {
          setUserRank(userBattleLegendsRank + 1);
        } else {
          const userTraineesRank = traineesOfMonth.findIndex(player => player.username === user?.username);
          if (userTraineesRank !== -1) {
            setUserRank(userTraineesRank + 1);
          } else {
            const userWarHeroesRank = warHeroes.findIndex(player => player.username === user?.username);
            if (userWarHeroesRank !== -1) {
              setUserRank(userWarHeroesRank + 1);
            } else {
              setUserRank(null);
            }
          }
        }
      }
    } catch (error) {
      console.error('Error fetching honor hall data:', error);
      setAssetStrategiesData([]);
      setTrainingStrategiesData([]);
      setBattleStrategiesData([]);
      setBattleLegendsData([]);
      setTraineesOfMonthData([]);
      setWarHeroesData([]);
    } finally {
      setLoading(false);
    }
  };

  const handleSubscribe = async (strategyId) => {
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${API_URL}/subscribe-strategy/${strategyId}`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`
        }
      });

      if (response.ok) {
        alert(t('honorHall.subscribeSuccess'));
        // Refresh data to update "Owned" status
        fetchData();
      } else {
        const errorData = await response.json();
        alert(`${t('honorHall.subscribeFailed')} ${errorData.error || 'Unknown error'}`);
      }
    } catch (error) {
      console.error('Error subscribing to strategy:', error);
      alert(t('honorHall.subscribeFailed'));
    }
  };

  const getCurrentData = () => {
    switch (activeTab) {
      case 'asset-strategies':
        return assetStrategiesData;
      case 'training-strategies':
        return trainingStrategiesData;
      case 'battle-strategies':
        return battleStrategiesData;
      case 'battle-legends':
        return battleLegendsData;
      case 'trainees-of-month':
        return traineesOfMonthData;
      case 'war-heroes':
        return warHeroesData;
      default:
        return [];
    }
  };

  const formatAverageMissionTime = (avgSeconds) => {
    if (avgSeconds == null || avgSeconds === 0) return '-';
    const totalSeconds = Math.round(Number(avgSeconds));
    const hours = Math.floor(totalSeconds / 3600);
    const minutes = Math.floor((totalSeconds % 3600) / 60);
    const seconds = totalSeconds % 60;
    const parts = [];
    if (hours > 0) parts.push(`${hours}h`);
    if (minutes > 0) parts.push(`${minutes}m`);
    if (seconds > 0 || parts.length === 0) parts.push(`${seconds}s`);
    return parts.join(' ');
  };

  const filteredData = isPlayerTab
    ? (Array.isArray(getCurrentData()) ? getCurrentData() : []).filter(player => 
        player.username && player.username.toLowerCase().includes(searchTerm.toLowerCase())
      )
    : (Array.isArray(getCurrentData()) ? getCurrentData() : []).filter(strategy => 
        (strategy.strategy_name && strategy.strategy_name.toLowerCase().includes(searchTerm.toLowerCase())) ||
        (strategy.player_name && strategy.player_name.toLowerCase().includes(searchTerm.toLowerCase()))
      );


  const totalPages = Math.ceil(filteredData.length / itemsPerPage);
  const startIndex = (currentPage - 1) * itemsPerPage;
  const endIndex = startIndex + itemsPerPage;
  const currentData = filteredData.slice(startIndex, endIndex);

  const handleShowMe = () => {
    if (isPlayerTab && userRank) {
      const targetPage = Math.ceil(userRank / itemsPerPage);
      setCurrentPage(targetPage);
      setShowUserRank(true);
    }
  };

  if (loading) {
    return (
      <div className="honor-hall-viewport-wrapper">
        <div 
          className="honor-hall-fullscreen-background"
          style={{ backgroundImage: 'url(/images/honorhall.png)' }}
        />
        <div 
          className={`honor-hall-viewport-container${isContainerMobile ? ' mobile' : ''}`}
          style={{
            width: `${containerBaseWidth}px`,
            height: `${containerBaseHeight}px`,
            transformOrigin: '0 0',
            transform: `translate(${viewportOffsetX}px, ${viewportOffsetY}px) scale(${viewportScale})`,
          }}
        >
          <div className="honor-hall-container">
            <div className="loading">{t('honorHall.loading')}</div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="honor-hall-viewport-wrapper">
      {/* Full-screen background - outside viewport-container to cover entire screen */}
      <div 
        className="honor-hall-fullscreen-background"
        style={{ backgroundImage: 'url(/images/honorhall.png)' }}
      />
      
      {/* Viewport container - 1920x1080 space that scales to fit screen */}
      <div 
        className={`honor-hall-viewport-container${isContainerMobile ? ' mobile' : ''}`}
        style={{
          width: `${containerBaseWidth}px`,
          height: `${containerBaseHeight}px`,
          transformOrigin: '0 0',
          transform: `translate(${viewportOffsetX}px, ${viewportOffsetY}px) scale(${viewportScale})`,
        }}
      >
        <div className="honor-hall-container">
      
          {user && (
            <div style={{ position: 'absolute', top: 10, left: 10, zIndex: 1000, display: 'flex', alignItems: 'center' }}>
              <FriendsIcon />
              <SettingsMenu onLogout={() => {
                playSoundEffect('/sounds/click.mp3');
                onLogout();
                navigate('/');
              }} />
              <span style={{ color: 'white', marginRight: '10px' }}>{user}</span>
            </div>
          )}

      <div className="honor-hall-header">
        <h1>{t('honorHall.title')}</h1>
        <button onClick={() => {
          playSoundEffect('/sounds/click.mp3');
          navigate('/welcome');
        }} className="back-button">
          {t('common.back')}
        </button>
      </div>

      <div className="table-controls">
        <div>
          <input
            type="text"
            placeholder={isPlayerTab ? t('honorHall.searchPlayers') : t('honorHall.searchStrategies')}
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="search-input"
          />
          {isPlayerTab && userRank && (
            <button onClick={() => {
              playSoundEffect('/sounds/click.mp3');
              handleShowMe();
            }} className="show-me-button">
              {t('honorHall.showMe')} ({t('honorHall.rank')} #{userRank})
            </button>
          )}
        </div>
        
        <div className="tabs">
          <button 
            className={`tab-golden ${activeTab === 'battle-legends' ? 'active' : ''}`}
            onClick={() => {
              playSoundEffect('/sounds/click.mp3');
              setActiveTab('battle-legends');
              setCurrentPage(1);
              setSearchTerm('');
            }}
          >
            {t('honorHall.battleLegends')}
          </button>
          <button 
            className={`tab-golden ${activeTab === 'battle-strategies' ? 'active' : ''}`}
            onClick={() => {
              playSoundEffect('/sounds/click.mp3');
              setActiveTab('battle-strategies');
              setCurrentPage(1);
              setSearchTerm('');
            }}
          >
            {t('honorHall.battleStrategies')}
          </button>
          <button 
            className={`tab-war-heroes ${activeTab === 'war-heroes' ? 'active' : ''}`}
            onClick={() => {
              playSoundEffect('/sounds/click.mp3');
              setActiveTab('war-heroes');
              setCurrentPage(1);
              setSearchTerm('');
            }}
          >
            {t('honorHall.warHeroes')}
          </button>
          <button 
            className={`tab-purple ${activeTab === 'trainees-of-month' ? 'active' : ''}`}
            onClick={() => {
              playSoundEffect('/sounds/click.mp3');
              setActiveTab('trainees-of-month');
              setCurrentPage(1);
              setSearchTerm('');
            }}
          >
            {t('honorHall.traineesOfMonth')}
          </button>
          <button 
            className={`tab-purple ${activeTab === 'training-strategies' ? 'active' : ''}`}
            onClick={() => {
              playSoundEffect('/sounds/click.mp3');
              setActiveTab('training-strategies');
              setCurrentPage(1);
              setSearchTerm('');
            }}
          >
            {t('honorHall.trainingStrategies')}
          </button>
          <button 
            className={`tab-blue ${activeTab === 'asset-strategies' ? 'active' : ''}`}
            onClick={() => {
              playSoundEffect('/sounds/click.mp3');
              setActiveTab('asset-strategies');
              setCurrentPage(1);
              setSearchTerm('');
            }}
          >
            {t('honorHall.assetStrategies')}
          </button>
        </div>
      </div>

      <div className={`table-container ${activeTab === 'war-heroes' ? 'war-heroes-table' : ''}`}>
        {activeTab === 'war-heroes' ? (
          <table className="honor-table war-heroes-table">
            <thead>
              <tr>
                <th>{t('honorHall.rank')}</th>
                <th>{t('honorHall.player')}</th>
                <th>{t('honorHall.averageMissionTime')}</th>
              </tr>
            </thead>
            <tbody>
              {currentData.map((player, index) => (
                <tr 
                  key={player.username}
                  className={showUserRank && player.username === user?.username ? 'highlighted' : ''}
                >
                  <td>{startIndex + index + 1}</td>
                  <td>{player.username}</td>
                  <td>{formatAverageMissionTime(player.avg_duration_seconds)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        ) : isPlayerTab ? (
          <table className="honor-table">
            <thead>
              <tr>
                <th>{t('honorHall.rank')}</th>
                <th>{t('honorHall.player')}</th>
                <th>{t('honorHall.winRate')}</th>
              </tr>
            </thead>
            <tbody>
              {currentData.map((player, index) => (
                <tr 
                  key={player.username}
                  className={showUserRank && player.username === user?.username ? 'highlighted' : ''}
                >
                  <td>{startIndex + index + 1}</td>
                  <td>{player.username}</td>
                  <td>{player.win_rate || 0}%</td>
                </tr>
              ))}
            </tbody>
          </table>
        ) : (
          <table className="honor-table">
            <thead>
              <tr>
                <th>{t('honorHall.rank')}</th>
                <th>{t('honorHall.player')}</th>
                <th>{t('honorHall.strategy')}</th>
                <th>{t('honorHall.winRate')}</th>
                <th>{t('honorHall.action')}</th>
              </tr>
            </thead>
            <tbody>
              {currentData.map((strategy, index) => (
                <tr key={`${strategy.strategy_id}-${strategy.player_id}`}>
                  <td>{startIndex + index + 1}</td>
                  <td>{strategy.player_name}</td>
                  <td>{strategy.strategy_name}</td>
                  <td>{strategy.win_rate}%</td>
                  <td>
                    {strategy.is_owned ? (
                      <span className="owned">{t('honorHall.owned')}</span>
                    ) : (
                      <button 
                        onClick={() => {
                          playSoundEffect('/sounds/click.mp3');
                          handleSubscribe(strategy.strategy_id);
                        }}
                        className="subscribe-button"
                      >
                        {t('honorHall.subscribe')}
                      </button>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>

      <div className="pagination">
        <button 
          onClick={() => {
            playSoundEffect('/sounds/click.mp3');
            setCurrentPage(prev => Math.max(1, prev - 1));
          }}
          disabled={currentPage === 1}
        >
          {t('honorHall.previous')}
        </button>
        <span>{t('honorHall.page')} {currentPage} {t('honorHall.of')} {totalPages}</span>
        <button 
          onClick={() => {
            playSoundEffect('/sounds/click.mp3');
            setCurrentPage(prev => Math.min(totalPages, prev + 1));
          }}
          disabled={currentPage === totalPages}
        >
          {t('honorHall.next')}
        </button>
      </div>

      {showTutorialHonorHall && createPortal(
        <TutorialMessageBox
          image="/images/tutorial_salute.png"
          text={t('honorHall.tutorialText')}
          onContinue={async () => {
            const token = localStorage.getItem('token');
            if (token) {
              await fetch(`${API_URL}/tutorial/mark-seen`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
                body: JSON.stringify({ messageKey: 'honor_hall' }),
              });
              setSeenTutorialMessages(prev => prev.includes('honor_hall') ? prev : [...prev, 'honor_hall']);
            }
            setShowTutorialHonorHall(false);
          }}
          voiceKey="honorHall.tutorialText"
        />,
        document.body
      )}
        </div>
      </div>
    </div>
  );
}

export default HonorHall;
